// src/contexts/CompensationsContext.tsx
// Compensi dipendenti caricati UNA volta all'apertura della pagina Dipendenti
// e condivisi tra la tab Compenso e il modal Performance. Il salvataggio
// aggiorna lo stato locale: nessun refetch a ogni apertura, nessun dato stale.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';

import { useAuth } from './AuthContext';
import {
    fetchCompensationsService,
    upsertCompensationService,
    EmployeeCompensation,
} from '@/services/employees/employeeCompensationService';

type UpsertPayload = Parameters<typeof upsertCompensationService>[0];

interface CompensationsContextValue {
    compensations: EmployeeCompensation[];
    loading: boolean;
    getByHairdresserId: (hairdresserId: string) => EmployeeCompensation | null;
    saveCompensation: (payload: UpsertPayload) => Promise<{ success: boolean; error?: string }>;
}

const CompensationsContext = createContext<CompensationsContextValue | undefined>(undefined);

export const CompensationsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { organization, isOwner } = useAuth();
    const shopId = Number(organization?.id);

    const [compensations, setCompensations] = useState<EmployeeCompensation[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        // Dati economici: solo il titolare. Per gli altri niente fetch.
        if (!isOwner || !organization?.id) {
            setCompensations([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        (async () => {
            const res = await fetchCompensationsService(shopId);
            if (!active) return;
            if (res.success) setCompensations(res.data);
            setLoading(false);
        })();
        return () => { active = false; };
    }, [isOwner, organization?.id, shopId]);

    const getByHairdresserId = useCallback(
        (hairdresserId: string) => compensations.find((c) => c.hairdresser_id === hairdresserId) || null,
        [compensations],
    );

    const saveCompensation = useCallback(async (payload: UpsertPayload) => {
        const res = await upsertCompensationService(payload);
        if (!res.success) return { success: false, error: res.error };
        const saved = res.data;
        setCompensations((prev) => [...prev.filter((c) => c.hairdresser_id !== saved.hairdresser_id), saved]);
        return { success: true };
    }, []);

    const value = useMemo(
        () => ({ compensations, loading, getByHairdresserId, saveCompensation }),
        [compensations, loading, getByHairdresserId, saveCompensation],
    );

    return <CompensationsContext.Provider value={value}>{children}</CompensationsContext.Provider>;
};

export const useCompensations = (): CompensationsContextValue => {
    const ctx = useContext(CompensationsContext);
    if (!ctx) throw new Error('useCompensations deve essere usato dentro un CompensationsProvider');
    return ctx;
};
